import type { LinkProps } from '@chakra-ui/react'
import { Link } from '@chakra-ui/react'
import type { MouseEvent, Ref } from 'react'
import { forwardRef } from 'react'

interface NavLinkProps extends LinkProps {
  isCurrent?: boolean
  isDisabled?: boolean
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void
}

export const NavLink = forwardRef(
  (
    {
      children,
      isCurrent = false,
      isDisabled = false,
      onClick,
      sx,
      ...rest
    }: NavLinkProps,
    ref: Ref<HTMLAnchorElement> | undefined,
  ) => {
    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()

      const href = e.currentTarget.href.split('#')[1]
      const elementId = href.replace(/#/, '')
      const top = document.getElementById(elementId)?.offsetTop

      window.scrollTo({
        top: top && top,
        behavior: 'smooth',
      })

      onClick?.(e)
    }

    const bg = isCurrent ? 'neutral.50' : 'transparent'

    let color = isCurrent ? 'primary.500' : 'primary.900'

    if (isDisabled) {
      color = 'neutral.500'
    }

    return (
      <Link
        ref={ref}
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          pr: 4,
          py: 3,
          bg,
          color,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          fontSize: 'sm',
          fontWeight: 'bold',
          textAlign: 'start',
          textDecoration: 'none',
          transition: 'color 0.2s ease-in-out',
          _hover: {
            color: !isDisabled && 'primary.500',
            textDecoration: 'none',
            _after: {
              opacity: !isDisabled && 1,
            },
          },
          _focus: {
            outline: 'none',
          },
          _after: {
            opacity: isCurrent ? 1 : 0,
            position: 'absolute',
            left: 'calc(100% - 3px)',
            content: '""',
            width: '3px',
            height: '100%',
            backgroundColor: 'primary.500',
            borderRadius: 'full',
            transition: 'opacity 0.2s ease-in-out',
          },
          ...sx,
        }}
        variant="link"
        onClick={handleClick}
        {...rest}
      >
        {children}
      </Link>
    )
  },
)
